import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TerminalFormModal } from './TerminalFormModal';
import './OverviewTab.css';

/**
 * Terminals Tab Component
 * Displays Terminal table for Merchant Admins with Reports page styling
 * Shows terminal name, status, and location
 * When onSaveTerminal is provided, exposes Add/Edit self-service actions.
 */
export function TerminalsTab({ terminals, accountId, onSaveTerminal }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingTerminal, setEditingTerminal] = useState(null);

  const canManage = typeof onSaveTerminal === 'function';

  function openCreateModal() {
    setModalMode('create');
    setEditingTerminal(null);
    setModalOpen(true);
  }

  function openEditModal(terminal) {
    setModalMode('edit');
    setEditingTerminal(terminal);
    setModalOpen(true);
  }

  async function handleModalSave(formData) {
    // Errors propagate to TerminalFormModal's inline error alert
    await onSaveTerminal(formData, modalMode, editingTerminal);
    setModalOpen(false);
  }

  const filteredTerminals = (terminals || []).filter(terminal => {
    if (!searchTerm) return true;
    const lowerSearch = searchTerm.toLowerCase();
    const terminalName = (terminal.name || terminal.terminal_id || '').toLowerCase();
    const terminalId = String(terminal.id || terminal.terminal_id || '');
    return terminalName.includes(lowerSearch) || terminalId.includes(lowerSearch);
  });

  const columnCount = canManage ? 4 : 3;

  // Status Chip Component
  function StatusChip({ active }) {
    return (
      <span className={`status-chip ${active ? 'positive' : 'neutral'}`}>
        {active ? 'Active' : 'Inactive'}
      </span>
    );
  }

  return (
    <div className="fi-table-section">
      <div className="fi-table-header">
        <h3 className="fi-table-title">Terminals</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {canManage && (
            <button className="action-button" onClick={openCreateModal}>
              <FontAwesomeIcon icon={['fal', 'plus']} style={{ marginRight: '6px' }} />
              Add Terminal
            </button>
          )}
          <div className="fi-search-container">
            <FontAwesomeIcon icon={['fal', 'search']} className="fi-search-icon" />
            <input
              type="text"
              className="fi-search-input"
              placeholder="Search terminals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="fi-table-container">
        <table className="fi-table">
          <thead>
            <tr>
              <th className="col-name">Terminal Name</th>
              <th className="col-status text-center">Status</th>
              <th className="col-location">Location</th>
              {canManage && <th className="col-actions text-center">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredTerminals.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="fi-table-empty">
                  <div className="fi-table-empty-icon">
                    <FontAwesomeIcon icon={['fal', 'tablet-alt']} />
                  </div>
                  {searchTerm ? 'No terminals match your search' : 'No terminals configured'}
                  {canManage && !searchTerm && (
                    <div style={{ marginTop: '12px' }}>
                      <button className="action-button" onClick={openCreateModal}>
                        Add Your First Terminal
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              filteredTerminals.map((terminal) => (
                <tr key={terminal.id || terminal.terminal_id}>
                  <td className="col-name">
                    <span className="fi-name-link">
                      {terminal.name || `Terminal ${terminal.terminal_id || terminal.id}`}
                    </span>
                    <div className="fi-id-caption">ID: {terminal.terminal_id || terminal.id}</div>
                  </td>
                  <td className="col-status text-center">
                    <StatusChip active={terminal.is_active} />
                  </td>
                  <td className="col-location">
                    {terminal.location || '-'}
                  </td>
                  {canManage && (
                    <td className="col-actions text-center">
                      <button
                        className="action-button"
                        onClick={() => openEditModal(terminal)}
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredTerminals.length > 0 && (
        <div className="fi-table-footer">
          <span>
            Showing {filteredTerminals.length} of {(terminals || []).length} Terminals
          </span>
        </div>
      )}

      {canManage && (
        <TerminalFormModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleModalSave}
          mode={modalMode}
          terminal={editingTerminal}
        />
      )}
    </div>
  );
}
